import { Capacitor } from '@capacitor/core';
import type { AppChannel } from '@mitang/shared';

/** True when running inside the Capacitor native shell (Android). */
export const isNative = Capacitor.isNativePlatform();

/** 'android' | 'ios' | 'web'. */
export const platform = Capacitor.getPlatform();

export const isWeb = platform === 'web';
export const isAndroid = platform === 'android';

/**
 * True when this shell was installed from the Play Store.
 *
 * Both shells load the SAME website, so a build-time flag in the web bundle
 * cannot tell them apart — the native build stamps its distribution channel
 * into the user agent instead (capacitor.config.ts → android.appendUserAgent,
 * driven by MITANG_DIST at `cap sync` time).
 *
 * Matters because Play forbids an app from pushing users to download APKs
 * outside the store: the sideload self-update gate must never run in a Play
 * build (see components/UpdateBanner.tsx).
 */
export const isPlayBuild = /AnywayApp\/play/i.test(
  typeof navigator === 'undefined' ? '' : navigator.userAgent,
);

/** Distribution channel of THIS install (see isPlayBuild for how Play is detected). */
export const appChannel: AppChannel = isPlayBuild ? 'play' : isNative ? 'sideload' : 'web';

export interface InstalledApp {
  channel: AppChannel;
  /** Android versionCode — undefined in a browser. */
  build?: number;
  /** Android versionName — undefined in a browser. */
  version?: string;
}

/**
 * What is installed on this device. Native shells answer from the package
 * manager via @capacitor/app; a browser has no build, only its channel. Never
 * throws — an unreadable build degrades to channel-only.
 */
export async function getInstalledApp(): Promise<InstalledApp> {
  if (!isNative) return { channel: appChannel };
  try {
    const { App } = await import('@capacitor/app');
    const info = await App.getInfo();
    const build = Number(info.build);
    return {
      channel: appChannel,
      ...(Number.isFinite(build) ? { build } : {}),
      ...(info.version ? { version: info.version } : {}),
    };
  } catch {
    return { channel: appChannel };
  }
}
